import { FC, useState } from 'react';
import { IonSkeletonText } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { OrganizationsState } from './OrganizationsState';
import {
    filterAlerts,
    filterBaselines,
    IOutcomeMetric
} from '@innerhive-internal/innerhive-common';
import { ClientWellbeingModal } from './insights/ClientWellbeingModal';
import { useAgencyInsights } from './insights/useAgencyInsights';
import { UnreadableClients } from './insights/UnreadableClients';
import { NothingToChart } from './insights/NothingToChart';
import { useInsightsFilter } from './insights/useInsightsFilter';
import { InsightsFilterBar } from './insights/InsightsFilterBar';
import { InsightsDemoBanner } from './insights/InsightsDemoBanner';
import { IInsightsReportsProps, InsightsReports } from './insights/InsightsReports';
import { InsightsToolbar } from './insights/InsightsToolbar';
import { IInsightsClientList, InsightsClientListModal } from './insights/InsightsClientListModal';

const useStyles = createUseStyles({
    page: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 18
    },

    loading: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12
    }
});

/**
 * Care intelligence for an agency: what the caseload is doing, and who to see today.
 *
 * One page from one digest. The caseload count comes first, then the reports in reading order, from
 * who to deal with first to what is on file; the caseload map's colour key narrows everything. A manager and a
 * dispatcher reading this together never see two different numbers for the same thing, because
 * every report is the same arithmetic over the same read of the caseload.
 *
 * A caseload too young to chart keeps every surface that needs no comparison; the ones that do wait
 * on its history and say what for.
 */
export const OrganizationInsights: FC<{ orgId: string; orgName: string }> = ({ orgId, orgName }) => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);
    const orgState = useInstance(OrganizationsState);

    const [openClientId, setOpenClientId] = useState<string | undefined>();
    const [clientList, setClientList] = useState<IInsightsClientList | undefined>();
    const narrowing = useInsightsFilter();
    const { filter, narrowed, openMeter } = narrowing;

    const {
        hasLoaded, nothingReadable, digest, wellbeing, outcomes, participation, historyProgress, isDemo, unreadableClients, retry,
        clientAvatar, clientReading
    } = useAgencyInsights(orgId);

    // The agency home lands here, so an unfinished read has to look like one. Rendering nothing left
    // the first thing an agency sees blank for as long as the caseload takes to read.
    if (!hasLoaded) {
        return (
            <div className={classes.loading} data-id="insights-loading">
                <IonSkeletonText animated style={{ '--border-radius': '11px', height: 74 }} />
                <IonSkeletonText animated style={{ '--border-radius': '12px', height: 220 }} />
                <IonSkeletonText animated style={{ '--border-radius': '12px', height: 180 }} />
            </div>
        );
    }

    const openCareMap = (careMapId: string) => history.push(`/care/${careMapId}`);

    // Not in demo mode: the sample's clients are invented and have no care map to open.
    const canOpenProfile = (careMapId: string): boolean => {
        const careMap = orgState.getOrganizationCareMap(careMapId);
        return !isDemo && careMap !== undefined && orgState.canViewClientCareMap(careMap);
    };

    const openWellbeing = openClientId
        ? wellbeing.find(candidate => candidate.careMapId === openClientId)
        : undefined;

    const shownAlerts = filterAlerts(digest, filter);
    const shownBaselines = filterBaselines(digest, filter);

    const openOutcome = (metric: IOutcomeMetric) => setClientList({
        title: metric.drillTitle,
        blurb: metric.drillBlurb,
        clients: metric.clients,
        driversLabel: metric.driversLabel,
        definition: metric.definition
    });

    const reports: IInsightsReportsProps = {
        digest,
        wellbeing,
        history: historyProgress,
        participation,
        outcomes,
        alerts: shownAlerts,
        kindNarrowed: filter.kind !== undefined,
        readingNarrowed: filter.reading,
        matchingIds: narrowed ? shownBaselines.map(baseline => baseline.careMapId) : undefined,
        narrowed,
        openMeter,
        clientAvatar,
        clientReading,
        onToggleMeter: narrowing.toggleMeter,
        onToggleReading: narrowing.toggleReading,
        onClearFilter: narrowing.clear,
        onOpenClient: setOpenClientId,
        onShowIncidents: () => narrowing.narrowToReading('incident'),
        onOpenOutcome: openOutcome,
        onOpenList: setClientList
    };

    return (
        <div className={classes.page} data-id="organization-insights" data-demo={String(isDemo)}>
            {isDemo && <InsightsDemoBanner />}

            <InsightsToolbar digest={digest} />

            {/* Directly under the count it qualifies, so the caseload count and the clients missing
                from it are read together, and far enough above the filter bar that it cannot be
                taken for a remark about the reader's current selection. */}
            <UnreadableClients clients={unreadableClients} onRetry={retry} />

            {narrowed && (
                <InsightsFilterBar
                    filter={filter}
                    clientCount={shownBaselines.length}
                    findingCount={shownAlerts.length}
                    onClear={narrowing.clear}
                />
            )}

            {nothingReadable && <NothingToChart />}

            {!nothingReadable && <InsightsReports {...reports} />}

            <InsightsClientListModal
                open={clientList}
                clientAvatar={clientAvatar}
                clientReading={clientReading}
                onSelectClient={careMapId => {
                    setClientList(undefined);
                    setOpenClientId(careMapId);
                }}
                onClose={() => setClientList(undefined)}
            />

            <ClientWellbeingModal
                wellbeing={openWellbeing}
                reading={openClientId ? clientReading(openClientId) : undefined}
                history={historyProgress}
                clientAvatar={clientAvatar}
                onOpenProfile={openClientId && canOpenProfile(openClientId) ? openCareMap : undefined}
                onClose={() => setOpenClientId(undefined)}
            />
        </div>
    );
};
